// src/components/Multiplayer/MultiplayerGameLobby.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useGame } from '../../contexts/GameContext';
import { useWallet } from '../../contexts/WalletContext';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useNavigate } from 'react-router-dom';
import CustomRoomModal from './CustomRoomModal';
import './MultiplayerGameLobby.css';

const MultiplayerGameLobby = () => {
  const { 
    loading, 
    error,
    clearError,
    createCustomRoom,
    formatCurrency
  } = useGame();
  
  const { 
    walletBalances 
  } = useWallet();
  
  const { currentUser } = useAuth();
  const { isConnected, connectionStatus, reconnect } = useWebSocket();
  const navigate = useNavigate();
  
  const [showCustomRoomModal, setShowCustomRoomModal] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [activeRooms, setActiveRooms] = useState(0);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [cancelledMessage, setCancelledMessage] = useState('');
  
  const refreshTimeout = useRef(null);

  useEffect(() => {
  const lobbyContainer = document.querySelector('.multiplayer-lobby');
  if (lobbyContainer) {
    const checkScroll = () => {
      if (lobbyContainer.scrollHeight > lobbyContainer.clientHeight) {
        lobbyContainer.classList.add('scrollable');
      } else {
        lobbyContainer.classList.remove('scrollable');
      }
    };
    
    // Check initially
    checkScroll();
    
    // Re-check on resize
    window.addEventListener('resize', checkScroll);
    
    return () => {
      window.removeEventListener('resize', checkScroll);
    };
  }
}, []);
  // Check connection on mount
  useEffect(() => {
    if (currentUser) {
      const timer = setTimeout(() => {
        setActiveRooms(Math.floor(Math.random() * 15) + 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [currentUser]);

  // Show a banner if the player was redirected here because a game was cancelled
  // (e.g. the other player disconnected before the game could start).
  useEffect(() => {
    try {
      const msg = sessionStorage.getItem('gameCancelledMessage');
      if (msg) {
        setCancelledMessage(msg);
        sessionStorage.removeItem('gameCancelledMessage');
        const t = setTimeout(() => setCancelledMessage(''), 8000);
        return () => clearTimeout(t);
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Handle manual refresh of active rooms
  const handleRefresh = useCallback(() => {
    if (refreshTimeout.current) {
      clearTimeout(refreshTimeout.current);
    }
    
    setLocalError(null);
    clearError && clearError();
    
    refreshTimeout.current = setTimeout(() => {
      setActiveRooms(Math.floor(Math.random() * 15) + 1);
      console.log('🔄 Refreshed active rooms count');
    }, 500);
  }, [clearError]);

  // Handle free play button click
  const handleFreePlayClick = () => {
    if (!currentUser) {
      setLocalError('Please log in to play free games.');
      return;
    }
    
    if (!isConnected || connectionStatus !== 'connected') {
      setLocalError('Please wait for connection to game server.');
      return;
    }
    
    navigate('/multiplayer/free-play');
  };

  // Handle custom room creation
  const handleCustomRoomSubmit = async (roomData) => {
    if (!currentUser) {
      setLocalError('Please log in to create custom rooms.');
      return;
    }
    
    if (!isConnected || connectionStatus !== 'connected') {
      setLocalError('Cannot create room: WebSocket connection lost. Please reconnect.');
      return;
    }
    
    setIsCreatingRoom(true);
    setLocalError(null);
    clearError && clearError();
    
    try {
      console.log('🎮 Creating custom room:', roomData);
      
      if (roomData.buyIn > 0) {
        const balance = walletBalances[roomData.currency] || 0;
        if (balance < roomData.buyIn) {
          throw new Error(`Insufficient ${roomData.currency} balance. You need ${formatCurrency(roomData.buyIn, roomData.currency)}, but have ${formatCurrency(balance, roomData.currency)}`);
        }
      }
      
      const result = await createCustomRoom(roomData);
      
      if (result && result.game) {
        navigate(`/game/${result.game.id}`);
        setShowCustomRoomModal(false);
      } else {
        throw new Error(result?.error || 'Failed to create custom room');
      }
    } catch (error) {
      console.error('❌ Failed to create custom room:', error);
      setLocalError(error.message || 'Failed to create custom room. Please try again.');
    } finally {
      setIsCreatingRoom(false);
    }
  };

  // Handle manual reconnect
  const handleReconnect = async () => {
    setLocalError(null);
    clearError && clearError();
    
    try {
      await reconnect();
    } catch (error) {
      console.error('❌ Reconnect failed:', error);
      setLocalError(`Reconnection failed: ${error.message}. Please refresh the page.`);
    }
  };

    // Handle navigation to cash rummy
  const handleCashRummy = () => {
    if (!currentUser) {
      setLocalError('Please log in to play cash games.');
      return;
    }
    
    if (!isConnected || connectionStatus !== 'connected') {
      setLocalError('Please wait for connection to game server.');
      return;
    }
    
    navigate('/cash-rummy');
  };

  // Handle navigation to tournaments
  const handleTournament = () => {
    if (!currentUser) {
      setLocalError('Please log in to play tournaments.');
      return;
    }
    
    if (!isConnected || connectionStatus !== 'connected') {
      setLocalError('Please wait for connection to game server.');
      return;
    }
    
    navigate('/tournament');
  };

  const displayError = localError || error;
  const showConnectionError = displayError?.includes('connection') || 
                              displayError?.includes('WebSocket') || 
                              displayError?.includes('disconnected');

  return (
    <div className="multiplayer-lobby">
      {/* Header - Compact version */}
      <div className="lobby-header">
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/games')}>
            ← Back
          </button>
          <h1 className="lobby-title">🎮 Rummy Multiplayer</h1>
          <p className="lobby-subtitle">Select your game type</p>
        </div>
        
        <div className="header-right">
          <div className="active-rooms">
            <span className="rooms-count">{activeRooms}</span>
            <span className="rooms-text">Active Rooms</span>
          </div>
          <button 
            className="refresh-btn"
            onClick={handleRefresh}
            disabled={!currentUser}
            title="Refresh"
          >
            🔄
          </button>
        </div>
      </div>

      {/* Error Display - Only show non-connection errors */}
      {displayError && !showConnectionError && (
        <div className="error-banner">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <span className="error-message">{displayError}</span>
            <button 
              onClick={() => {
                setLocalError(null);
                clearError && clearError();
              }}
              className="dismiss-btn"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Game cancelled banner — shown when redirected here after a game was cancelled */}
      {cancelledMessage && (
        <div className="error-banner" style={{ border: '1px solid #FF9800', background: 'rgba(255, 152, 0, 0.15)' }}>
          <div className="error-content">
            <span className="error-icon">🚫</span>
            <span className="error-message">{cancelledMessage}</span>
            <button onClick={() => setCancelledMessage('')} className="dismiss-btn">Dismiss</button>
          </div>
        </div>
      )}

      {/* Main Game Type Selection - 4 compact cards */}
      {currentUser && (
        <div className="game-types-container">
          <div className="game-types-grid">
            {/* FREE PLAY CARD */}
            <div className="game-type-card free-play">
              <div className="card-header">
                <div className="card-icon">🎮</div>
                <div className="card-content">
                  <h3 className="card-title">Free Play</h3>
                  <div className="card-features">
                    <div className="feature">2-6 Players</div>
                    <div className="feature">No Entry Fee</div>
                    <div className="feature">Auto Match</div>
                  </div>
                </div>
              </div>
              <button 
                className="play-button"
                onClick={handleFreePlayClick}
                disabled={!isConnected || connectionStatus !== 'connected'}
                title={!isConnected ? 'Please connect to game server' : 'Go to free play'}
              >
                Play Now
              </button>
            </div>

                        {/* CASH RUMMY CARD */}
            <div className="game-type-card cash-rummy">
              <div className="card-header">
                <div className="card-icon">💰</div>
                <div className="card-content">
                  <h3 className="card-title">Cash Rummy</h3>
                  <div className="card-features">
                    <div className="feature">Real Money Games</div>
                    <div className="feature">Auto Match</div>
                    <div className="feature">Win Real Cash</div>
                  </div>
                </div>
              </div>
              <button 
                className="play-button"
                onClick={handleCashRummy}
                disabled={!isConnected || connectionStatus !== 'connected'}
              >
                Play Now
              </button>
            </div>

            {/* TOURNAMENT CARD */}
            <div className="game-type-card tournament">
              <div className="card-header">
                <div className="card-icon">🏆</div>
                <div className="card-content">
                  <h3 className="card-title">Tournament</h3>
                  <div className="card-features">
                    <div className="feature">Big Prize Pools</div>
                    <div className="feature">Scheduled Events</div>
                    <div className="feature">Compete & Win</div>
                  </div>
                </div>
              </div>
              <button 
                className="play-button"
                onClick={handleTournament}
                disabled={!isConnected || connectionStatus !== 'connected'}
              >
                Play Now
              </button>
            </div>

            {/* CUSTOM ROOM CARD */}
            <div className="game-type-card custom-room">
              <div className="card-header">
                <div className="card-icon">🎪</div>
                <div className="card-content">
                  <h3 className="card-title">Custom Room</h3>
                  <div className="card-features">
                    <div className="feature">Set Rules</div>
                    <div className="feature">Private Games</div>
                    <div className="feature">Invite Friends</div>
                  </div>
                </div>
              </div>
              <button 
                className="create-room-button"
                onClick={() => setShowCustomRoomModal(true)}
                disabled={!isConnected || connectionStatus !== 'connected'}
                title={!isConnected ? 'Please connect to game server' : 'Create or join a custom room'}
              >
                Create / Join
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM ROOM MODAL */}
      {showCustomRoomModal && (
        <CustomRoomModal
          onClose={() => {
            if (!isCreatingRoom) {
              setShowCustomRoomModal(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default MultiplayerGameLobby;